import { CustomSettings } from "subModule/src/services/invoice/types";

export type ModernInvoiceLineItem = {
  type: string;
  title: string;
  code: string;
  description: string;
  quantity: number;
  qtyLabel: string;
  unitPrice: number;
  discount: number;
  tax: number;
  amount: number;
};

export type ModernInvoicePayment = {
  details: string;
  number: string;
  date: string;
  mode: string;
  amount: number;
};

export type ModernInvoiceDeliveryNote = {
  vehicleName: string;
  vehiclePlateNo: string;
  deliveryNoteNo: string;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
};

/** Shape expected by ModernInvoicePreview (dummy or real invoice). */
export type ModernInvoiceViewData = {
  invoiceNo: string;
  date: string;
  status: string;
  statusAr: string;
  company: {
    name: string;
    tagline: string;
    phone: string;
    email: string;
    address: string;
    vat: string;
  };
  customer: {
    name: string;
    phone: string;
    email: string;
    address: string;
    vat: string;
  };
  vehicle: {
    make: string;
    model: string;
    year: string;
    plateNumber: string;
    fuelType: string;
    mileage: string;
    vin: string;
    color: string;
  };
  items: ModernInvoiceLineItem[];
  subtotal: number;
  discount: number;
  netSubtotal: number;
  tax: number;
  total: number;
  paidAmount: number;
  dueAmount: number;
  payments: ModernInvoicePayment[];
  deliveryNotes: ModernInvoiceDeliveryNote[];
  footerText: string;
  logoUrl?: string;
  notesText?: string;
  termsText?: string;
  termsTextSecondary?: string;
};

function formatItemDescription(item: any, settings: CustomSettings): string {
  const opts = settings.itemDisplayOptions;
  const title = item.title || item.itemName || item.packageName || "";
  const code =
    item.code || item.itemCode || item.serviceCode || item.packageCode || "";

  if (opts?.showCode && !opts?.showTitle) return code || title;
  if (opts?.showTitle && opts?.showCode && code) return `${title} (${code})`;
  return title || code || item.description || "";
}

function qtyLabelFor(type: string, quantity: number, unitTitle?: string): string {
  if (unitTitle) return `${quantity} ${unitTitle}`;
  if (type === "package") return `${quantity} Package`;
  if (type === "service") return `${quantity} Service`;
  return `${quantity} Item`;
}

function pushLineItem(
  result: ModernInvoiceLineItem[],
  raw: any,
  type: string,
  settings: CustomSettings,
) {
  const quantity = Number(raw.quantity) || 1;
  result.push({
    type,
    title: raw.title || raw.itemName || raw.packageName || "",
    code:
      raw.code ||
      raw.itemCode ||
      raw.serviceCode ||
      raw.packageCode ||
      "",
    description: raw.description || formatItemDescription(raw, settings),
    quantity,
    qtyLabel: qtyLabelFor(type, quantity, raw.unitTitle),
    unitPrice: Number(raw.unitPrice ?? raw.price) || 0,
    discount: Number(raw.discount) || 0,
    tax: Number(raw.taxAmount ?? raw.tax) || 0,
    amount: Number(raw.totalAmount ?? raw.amount) || 0,
  });
}

function collectLineItems(
  invoice: any,
  settings: CustomSettings,
): ModernInvoiceLineItem[] {
  const result: ModernInvoiceLineItem[] = [];

  (invoice.invoicePackages || []).forEach((pkg: any) => {
    pushLineItem(result, pkg, "package", settings);
    (pkg.services || []).forEach((s: any) => {
      pushLineItem(result, s, "service", settings);
      (s.items || []).forEach((i: any) =>
        pushLineItem(result, i, "item", settings),
      );
    });
    (pkg.items || []).forEach((i: any) =>
      pushLineItem(result, i, "item", settings),
    );
  });

  (invoice.invoiceServices || []).forEach((s: any) => {
    pushLineItem(result, s, "service", settings);
    (s.items || []).forEach((i: any) =>
      pushLineItem(result, i, "item", settings),
    );
  });

  (invoice.invoiceItems || []).forEach((i: any) =>
    pushLineItem(result, i, "item", settings),
  );

  return result;
}

function collectDeliveryNotes(invoice: any): ModernInvoiceDeliveryNote[] {
  const notes =
    invoice.deliveryNoteResponses ||
    invoice.deliveryNotes ||
    invoice.consolidatedDeliveryNotes ||
    invoice.invoiceDeliveryNotes ||
    [];

  return (notes as any[]).map((note: any) => ({
    vehicleName:
      note.vehicleName ||
      [note.attributes?.makeTitle, note.attributes?.modelTitle, note.attributes?.year]
        .filter(Boolean)
        .join(" ") ||
      note.vehicle?.name ||
      "",
    vehiclePlateNo:
      note.vehiclePlateNo || note.plateNumber || note.attributes?.plateNumber || "",
    deliveryNoteNo: note.deliveryNoteNo || note.deliveryNoteNumber || "",
    subtotal: Number(note.subtotalAmount ?? note.subTotal ?? note.subtotal) || 0,
    discount: Number(note.discount) || 0,
    tax: Number(note.taxAmount ?? note.tax) || 0,
    total: Number(note.totalAmount ?? note.total) || 0,
  }));
}

function collectPayments(invoice: any): ModernInvoicePayment[] {
  const details = invoice?.paymentDetail || [];
  if (!Array.isArray(details) || !details.length) return [];

  return details.map((payment: any, index: number) => ({
    details: payment.details || payment.remarks || payment.description || "",
    number: payment.typeNumber || payment.paymentNo || `PAY-${index + 1}`,
    date: payment.date || payment.paymentDate || "",
    mode: payment.mode || payment.paymentMode || "",
    amount: Number(payment.amount) || 0,
  }));
}

function resolvePaymentStatus(
  invoice: any,
  t?: (key: string) => string,
): { status: string; statusAr: string } {
  const code = invoice?.paymentStatus;
  if (code === 777) {
    return {
      status: t?.("unpaid") || "UNPAID",
      statusAr: "غير مدفوع",
    };
  }
  if (code === 778) {
    return {
      status: t?.("partialPaid") || "PARTIAL PAID",
      statusAr: "مدفوع جزئياً",
    };
  }
  if (code === 779 || code != null) {
    return {
      status: t?.("paid") || "PAID",
      statusAr: "مدفوع",
    };
  }
  return {
    status: t?.("paymentDue") || "PAYMENT DUE",
    statusAr: "مستحق الدفع",
  };
}

/** Map a live invoice (+ template settings) into ModernInvoicePreview view data. */
export function mapInvoiceToModernData(
  invoice: any,
  settings: CustomSettings,
  options?: { t?: (key: string) => string; logoUrl?: string },
): ModernInvoiceViewData {
  const attrs = invoice?.attributes || {};
  // Classic preview uses `subtotalAmount` — prefer it so summary rows aren't 0.
  const subtotal =
    Number(
      invoice?.subtotalAmount ?? invoice?.subTotal ?? invoice?.subtotal,
    ) || 0;
  const discount = Number(invoice?.discount) || 0;
  const tax = Number(invoice?.taxAmount ?? invoice?.tax) || 0;
  const total = Number(invoice?.totalAmount ?? invoice?.total) || 0;
  const dueAmount =
    Number(invoice?.dueAmount ?? invoice?.balanceAmount ?? invoice?.balance) ||
    0;
  const paidAmount =
    Number(invoice?.paidAmount) ||
    Math.max(0, total - (Number(invoice?.balanceAmount) || dueAmount));
  const { status, statusAr } = resolvePaymentStatus(invoice, options?.t);
  const vatValue = attrs.stationTaxNumber || attrs.stationVat || "";

  return {
    invoiceNo: invoice?.invoiceNo || invoice?.invoiceNumber || "",
    date: invoice?.invoiceDate || invoice?.createdAt || new Date().toISOString(),
    status,
    statusAr,
    company: {
      name: attrs.stationName || "",
      tagline: attrs.stationTagline || attrs.stationSecondaryName || "",
      phone: attrs.stationPhone || attrs.stationPhoneNo2 || "",
      email: attrs.stationEmail || "",
      address: attrs.stationAddress || "",
      vat: vatValue
        ? vatValue.toString().startsWith("TRN")
          ? vatValue
          : `TRN: ${vatValue}`
        : "",
    },
    customer: {
      name: attrs.customerName || attrs.companyName || "",
      phone: attrs.customerPhone || "",
      email: attrs.customerEmail || attrs.companyEmail || "",
      address: attrs.customerAddress || "",
      vat: attrs.customerTaxNumber
        ? attrs.customerTaxNumber.toString().startsWith("TRN")
          ? attrs.customerTaxNumber
          : `TRN: ${attrs.customerTaxNumber}`
        : "",
    },
    vehicle: {
      make: attrs.makeTitle || attrs.make || "",
      model: attrs.modelTitle || attrs.model || "",
      year: attrs.year != null ? String(attrs.year) : "",
      plateNumber:
        invoice?.vehiclePlateNo ||
        attrs.plateNumber ||
        attrs.vehiclePlateNo ||
        "",
      fuelType: attrs.fuelTypeTitle || attrs.fuelType || "",
      mileage: (() => {
        const raw =
          attrs.odometer ??
          attrs.mileage ??
          invoice?.odometer ??
          invoice?.mileage;
        return raw != null && raw !== "" ? String(raw) : "";
      })(),
      vin: invoice?.vehicleVin || attrs.vin || "",
      color: attrs.colorTitle || attrs.color || "",
    },
    items: collectLineItems(invoice, settings),
    subtotal,
    discount,
    netSubtotal: Math.max(0, subtotal - discount),
    tax,
    total,
    paidAmount,
    dueAmount,
    payments: collectPayments(invoice),
    deliveryNotes: collectDeliveryNotes(invoice),
    footerText:
      settings.footerText ||
      "This is system generated invoice.",
    logoUrl:
      options?.logoUrl ||
      attrs.stationImgUrl ||
      attrs.stationLogo ||
      attrs.stationLogoUrl ||
      attrs.logo ||
      "",
    notesText: invoice?.notes || attrs.notes || "",
    termsText:
      attrs.stationTermAndCondition ||
      invoice?.terms ||
      attrs.terms ||
      "",
    termsTextSecondary:
      attrs.stationTermAndConditionSecondary ||
      invoice?.termsSecondary ||
      attrs.termsSecondary ||
      "",
  };
}
